import { ReactNode, useCallback, useMemo, useState } from 'react';
import { FormApi } from 'final-form';

import { Page, snackbar } from '@occc-app/design-system';
import { Offer_Engagement_Type, OfferFragment } from '@occc/graphql';

import { getOfferNameAndDescriptionInitialValues } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionInitialValues';
import copies from '@occc-app/routes/CampaignComponents/copies';
import { useGetOfferOperationHandlers } from '@occc-app/offerHelpers/operations/useGetOfferOperationHandlers';
import { GetConfigureFormOperations } from '@occc-app/offerHelpers/types';
import { useOfferCreatePageController } from '@occc-app/routes/Offer/OfferCreatePage/useOfferCreatePageControllerHook';
import { OfferType } from '@occc-app/offerValidationService/types';
import { ConfigurationFormNameAndDescriptionValues } from '@occc-app/routes/Offer/types';
import { OperationResult, runOperations } from '@occc-app/offerHelpers/operations/runOperations';

type OfferCreateConfigurePageProps<Values, InitialValues> = {
  pageTitle: string;
  subtitle?: string;
  offerType: OfferType;
  engagementType: Offer_Engagement_Type;
  campaignId?: string;
  onOfferCreated?: (offerId: string) => void;
  onConfigureOperationsCompleted?: (operationResults: OperationResult[], offerId: string) => void;
  getConfigureOperations: GetConfigureFormOperations<Values>;
  getInitialValues: () => InitialValues;
  renderConfigureForm: (args: {
    onSubmit: (values: Values, form: FormApi<Values, InitialValues>) => Promise<void>;
    initialValues: InitialValues;
    loading: boolean;
    executing: boolean;
  }) => ReactNode;
};

export const OfferCreateConfigurePage = <Values, InitialValues>({
  pageTitle,
  subtitle,
  offerType,
  engagementType,
  campaignId,
  onOfferCreated,
  onConfigureOperationsCompleted,
  getConfigureOperations,
  getInitialValues,
  renderConfigureForm,
}: OfferCreateConfigurePageProps<Values, InitialValues>) => {
  const { offerTitle, onCreate, loading } = useOfferCreatePageController({
    offerType,
    onCompleted: () => {},
    extraMutationVariables: { campaignUuid: campaignId },
  });

  const [executing, setExecuting] = useState(false);

  const initialValues: InitialValues = useMemo(() => {
    const base = getInitialValues();
    return {
      ...base,
      ...getOfferNameAndDescriptionInitialValues(undefined),
    };
  }, [getInitialValues]);

  const operationHandlers = useGetOfferOperationHandlers();

  const executeOperationsForNewOffer = useCallback(
    async (
      newOfferId: string,
      newOffer: OfferFragment,
      values: Values,
      dirtyFields: { [key: string]: boolean },
    ) => {
      // Avoid redundant name/description update right after create
      const maskedDirtyFields = { ...dirtyFields, name: false, description: false };

      const operations = getConfigureOperations(newOfferId, newOffer, values, maskedDirtyFields);
      return runOperations(operations, operationHandlers);
    },
    [getConfigureOperations, operationHandlers],
  );

  const onSubmit = async (
    values: Values & ConfigurationFormNameAndDescriptionValues,
    form: FormApi<Values, InitialValues>,
  ) => {
    const formState = form.getState();
    try {
      const result = await onCreate({
        name: values.name,
        description: values.description || undefined,
        engagementType,
      });

      const offer: OfferFragment = result.data.offerCreate;

      if (!offer) {
        throw new Error('Failed to create Offer');
      }

      setExecuting(true);
      const operationResults = await executeOperationsForNewOffer(
        offer.id,
        offer,
        values,
        formState.dirtyFields,
      );

      snackbar.success(copies.onSubmitSuccessToastMessage);
      onConfigureOperationsCompleted?.(operationResults, offer.id);
      onOfferCreated?.(offer.id);
    } catch (error: any) {
      snackbar.error(error?.message || 'Failed to create Offer');
    } finally {
      setExecuting(false);
    }
  };

  return (
    <Page
      browserTabTitle={`${pageTitle} ${offerTitle}`}
      pageHeaderProps={{ title: pageTitle, subtitle }}
      containerProps={{ variant: 'md' }}
    >
      {renderConfigureForm({
        onSubmit,
        initialValues,
        loading,
        executing,
      })}
    </Page>
  );
};
